import React from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PsychologyIcon from "@mui/icons-material/Psychology";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import SportsIcon from "@mui/icons-material/Sports";
import MedicationIcon from "@mui/icons-material/Medication";
import HelpIcon from "@mui/icons-material/Help";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import QuizIcon from "@mui/icons-material/Quiz";
import { weeklyReportHasActivities } from "../../../../../utils/weeklyReportActivities";

const ALL_SECTIONS = [
  {
    id: "ai-insights",
    label: "AI Insights",
    icon: <PsychologyIcon sx={{ fontSize: 16 }} />,
  },
  {
    id: "extra-data",
    label: "Extra Data",
    icon: <QuizIcon sx={{ fontSize: 16 }} />,
  },
  {
    id: "nutrition",
    label: "Health",
    icon: <HealthAndSafetyIcon sx={{ fontSize: 16 }} />,
  },
  {
    id: "exercise",
    label: "Exercise",
    icon: <FitnessCenterIcon sx={{ fontSize: 16 }} />,
  },
  {
    id: "activities",
    label: "Activities",
    icon: <SportsIcon sx={{ fontSize: 16 }} />,
  },
  {
    id: "cardio",
    label: "Cardio",
    icon: <DirectionsRunIcon sx={{ fontSize: 16 }} />,
  },
  {
    id: "supplements",
    label: "Supplements",
    icon: <MedicationIcon sx={{ fontSize: 16 }} />,
  },
  {
    id: "questions",
    label: "Questions",
    icon: <HelpIcon sx={{ fontSize: 16 }} />,
  },
  {
    id: "photos",
    label: "Photos",
    icon: <PhotoCameraIcon sx={{ fontSize: 16 }} />,
  },
];

const ReportNavigation = ({ report }) => {
  const sections = React.useMemo(() => {
    const hasActivities = weeklyReportHasActivities(report);
    return ALL_SECTIONS.filter(
      (section) => section.id !== "activities" || hasActivities,
    );
  }, [report]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const navHeight = 130; // Height of navigation bar + some padding
      const yOffset = -navHeight; // Negative offset to account for fixed nav
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <Box
      sx={(theme) => ({
        position: "sticky",
        top: 16,
        zIndex: 1,
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        py: 1.5,
        px: 3,
        mx: 0,
        mb: 3,
        display: "flex",
        gap: 1.5,
        overflowX: "auto",
        borderRadius: theme.spacing(2),
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        "&::-webkit-scrollbar": {
          height: 6,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0,0,0,0.1)",
          borderRadius: 3,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(0,0,0,0.2)",
          borderRadius: 3,
        },
      })}
    >
      {" "}
      {sections.map((section) => (
        <Chip
          key={section.id}
          icon={section.icon}
          label={section.label}
          onClick={() => scrollToSection(section.id)}
          sx={{
            "&:hover": {
              bgcolor: "primary.light",
              color: "primary.contrastText",
            },
          }}
          size="small"
        />
      ))}
    </Box>
  );
};

export const BackToTop = () => (
  <Tooltip title="Back to top">
    <IconButton
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      sx={{
        position: "absolute",
        right: 48,
        top: 8,
        bgcolor: "background.paper",
        boxShadow: 1,
        border: "1px solid",
        borderColor: "divider",
        "&:hover": {
          bgcolor: "primary.light",
          color: "primary.contrastText",
        },
      }}
      size="small"
    >
      <KeyboardArrowUpIcon />
    </IconButton>
  </Tooltip>
);

export default ReportNavigation;
